import { normalizeColorToCanonical } from './colorUtils';
import { SEZIONI_AWG } from '../constants/cableConstants';

export const normalizeInternalCableCode = (value) => String(value || '').trim();
export const normalizeCableDescription = (value) => String(value || '').trim().slice(0, 160);

export const normalizeCatalogItemColors = (item, catalogType) => {
  const nextItem = { ...(item || {}) };

  if (catalogType === 'multipolari') {
    nextItem.coloreGuaina = normalizeColorToCanonical(nextItem.coloreGuaina);
    nextItem.poliColori = Array.isArray(nextItem.poliColori)
      ? nextItem.poliColori.map((color) => normalizeColorToCanonical(color))
      : [];
  } else {
    nextItem.colore = normalizeColorToCanonical(nextItem.colore);
  }

  return nextItem;
};

const normalizeCatalogSearchText = (value) => String(value ?? '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLocaleLowerCase()
  .trim();

export const matchesCatalogSearch = (item, searchValue) => {
  const query = normalizeCatalogSearchText(searchValue);
  if (!query) return true;

  return [
    item?.codiceInterno,
    item?.descrizione,
    item?.produttore,
    item?.codiceCostruttore,
    item?.colore,
    item?.sezione,
    item?.calibroAWG,
    item?.numeroPoli,
    item?.coloreGuaina,
    ...(Array.isArray(item?.poliColori) ? item.poliColori : [])
  ].some((value) => normalizeCatalogSearchText(value).includes(query));
};

export const getCatalogSectionStandard = (item) => item?.standardSezione === 'awg' ? 'awg' : 'metric';

export const isCatalogItemCompatibleWithProject = (item, useAWG = false) => (
  getCatalogSectionStandard(item) === (useAWG ? 'awg' : 'metric')
);

export const getCatalogSectionDisplay = (item) => {
  if (getCatalogSectionStandard(item) === 'awg') {
    const gauge = Number.parseFloat(item?.calibroAWG);
    return Number.isFinite(gauge) ? `${gauge} AWG` : '—';
  }

  const metric = Number.parseFloat(item?.sezione);
  return Number.isFinite(metric) ? `${metric} mm²` : '—';
};

export const getConfiguredAWGSections = (sections = SEZIONI_AWG) => (
  Array.isArray(sections) && sections.length > 0 ? sections : SEZIONI_AWG
);

export const findCatalogAWGSectionByGauge = (gauge, sections = SEZIONI_AWG) => {
  const numericGauge = Number.parseFloat(gauge);
  if (!Number.isFinite(numericGauge)) return null;
  return getConfiguredAWGSections(sections).find((section) => Number(section?.val) === numericGauge) || null;
};

export const findCatalogAWGSectionByMetric = (metric, sections = SEZIONI_AWG) => {
  const numericMetric = Number.parseFloat(metric);
  if (!Number.isFinite(numericMetric)) return null;
  return getConfiguredAWGSections(sections).find((section) => Math.abs(Number(section?.metric) - numericMetric) < 0.000001) || null;
};

export const changeCatalogSectionStandard = (item, standard, sections = SEZIONI_AWG) => {
  const nextItem = { ...(item || {}) };
  nextItem.standardSezione = standard === 'awg' ? 'awg' : 'metric';

  if (nextItem.standardSezione === 'awg') {
    const matchingAWG = findCatalogAWGSectionByMetric(nextItem.sezione, sections);
    nextItem.calibroAWG = matchingAWG?.val || '';
    if (matchingAWG) nextItem.sezione = Number(matchingAWG.metric);
  } else {
    delete nextItem.calibroAWG;
  }

  return nextItem;
};

export const changeCatalogAWGGauge = (item, gauge, sections = SEZIONI_AWG) => {
  const matchingAWG = findCatalogAWGSectionByGauge(gauge, sections);
  return {
    ...(item || {}),
    standardSezione: 'awg',
    calibroAWG: matchingAWG?.val || '',
    sezione: matchingAWG ? Number(matchingAWG.metric) : ''
  };
};

export const normalizeCatalogItemSection = (item, sections = SEZIONI_AWG) => {
  const nextItem = { ...(item || {}), standardSezione: getCatalogSectionStandard(item) };
  if (nextItem.standardSezione === 'awg') {
    const matchingAWG = findCatalogAWGSectionByGauge(nextItem.calibroAWG, sections);
    if (!matchingAWG) return { isValid: false, reason: 'awg-required' };
    nextItem.calibroAWG = Number(matchingAWG.val);
    nextItem.sezione = Number(matchingAWG.metric);
    return { isValid: true, item: nextItem };
  }

  const metricSection = Number.parseFloat(nextItem.sezione);
  if (!Number.isFinite(metricSection) || metricSection <= 0) {
    return { isValid: false, reason: 'section-required' };
  }
  nextItem.sezione = metricSection;
  delete nextItem.calibroAWG;
  return { isValid: true, item: nextItem };
};

const normalizeCodeKey = (value) => normalizeInternalCableCode(value).toLocaleLowerCase();

const getCatalogItems = (catalog = {}, type = null) => {
  if (type === 'unipolari' || type === 'multipolari') {
    return Array.isArray(catalog?.[type]) ? catalog[type] : [];
  }

  return [
    ...(Array.isArray(catalog?.unipolari) ? catalog.unipolari : []),
    ...(Array.isArray(catalog?.multipolari) ? catalog.multipolari : [])
  ];
};

const isCatalogCodeUnique = (catalog, code, excludedItemId = null) => {
  const normalizedCode = normalizeCodeKey(code);
  if (!normalizedCode) return false;

  return !getCatalogItems(catalog).some((item) => (
    item?.id !== excludedItemId && normalizeCodeKey(item?.codiceInterno) === normalizedCode
  ));
};

export const validateCatalogItem = (catalog, item) => {
  const codiceInterno = normalizeInternalCableCode(item?.codiceInterno);
  if (!codiceInterno) return { isValid: false, reason: 'required' };
  if (!isCatalogCodeUnique(catalog, codiceInterno, item?.id)) {
    return { isValid: false, reason: 'duplicate' };
  }
  return { isValid: true, codiceInterno };
};

export const findCatalogItemForCable = (cable, catalog = {}) => {
  const assignment = cable?.catalogAssignment;
  if (!assignment) return null;

  const items = getCatalogItems(catalog, Array.isArray(cable?.poli) ? 'multipolari' : 'unipolari');
  const byId = assignment.itemId
    ? items.find((item) => String(item?.id) === String(assignment.itemId))
    : null;
  if (byId) return byId;

  const codeKey = normalizeCodeKey(assignment.codiceInterno);
  if (!codeKey) return null;
  const codeMatches = items.filter((item) => normalizeCodeKey(item?.codiceInterno) === codeKey);
  return codeMatches.length === 1 ? codeMatches[0] : null;
};

const parseCatalogSection = (value, fallback = 0.5) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const createDefaultPole = (cable, index) => ({
  id: Date.now() + index,
  colore: 'ORANGE',
  sezione: 0.5,
  siglaturaA: '',
  siglaturaB: '',
  lunghezzaA: cable?.lunghezza || 200,
  lunghezzaB: cable?.lunghezza || 200,
  terminaleA: 'PSW',
  terminaleB: 'PSW',
  collegamentoA: '',
  collegamentoB: ''
});

export const assignCatalogItemToCable = (cable, item, { forceStructure = false } = {}) => {
  if (!item) return cable;

  const assignment = {
    itemId: item.id || null,
    codiceInterno: normalizeInternalCableCode(item.codiceInterno)
  };
  const catalogDescription = normalizeCableDescription(item.descrizione);

  if (!Array.isArray(cable?.poli)) {
    return {
      ...cable,
      catalogAssignment: assignment,
      _catalogStatus: 'resolved',
      _catalogDescription: catalogDescription || undefined,
      colore: normalizeColorToCanonical(item.colore || cable.colore) || cable.colore,
      sezione: parseCatalogSection(item.sezione, cable.sezione)
    };
  }

  const configuredCount = Math.max(0, Number.parseInt(item.numeroPoli, 10) || 0);
  const existingPoles = Array.isArray(cable.poli) ? cable.poli : [];
  const hasStructureMismatch = configuredCount > 0 && configuredCount !== existingPoles.length;
  const targetCount = forceStructure && configuredCount > 0 ? configuredCount : existingPoles.length;
  const section = parseCatalogSection(item.sezione, existingPoles[0]?.sezione || 0.5);
  const poleColors = Array.isArray(item.poliColori) ? item.poliColori : [];
  const poles = Array.from({ length: targetCount }, (_, index) => {
    const basePole = existingPoles[index] || createDefaultPole(cable, index);
    return {
      ...basePole,
      colore: normalizeColorToCanonical(poleColors[index] || basePole.colore || 'ORANGE') || 'ORANGE',
      sezione: section
    };
  });

  return {
    ...cable,
    catalogAssignment: assignment,
    _catalogStatus: hasStructureMismatch && !forceStructure ? 'structure-mismatch' : 'resolved',
    _catalogDescription: catalogDescription || undefined,
    poli: poles
  };
};

export const unlinkCableFromCatalog = (cable) => {
  const { catalogAssignment, _catalogStatus, _catalogDescription, ...rest } = cable || {};
  return rest;
};

export const reconcileCableWithCatalog = (cable, catalog = {}) => {
  if (!cable?.catalogAssignment) return cable;
  const item = findCatalogItemForCable(cable, catalog);

  if (!item) {
    const { _catalogDescription, ...withoutDescription } = cable;
    return withoutDescription._catalogStatus === 'unresolved'
      ? withoutDescription
      : { ...withoutDescription, _catalogStatus: 'unresolved' };
  }

  return assignCatalogItemToCable(cable, item);
};

export const reconcileCablesWithCatalog = (cables = [], catalog = {}) => (
  cables.map((cable) => reconcileCableWithCatalog(cable, catalog))
);

export const getCableCatalogStatus = (cable) => {
  if (!cable?.catalogAssignment) return 'generic';
  return cable._catalogStatus || 'resolved';
};

export const createCatalogAssignment = (itemId, codiceInterno) => {
  const normalizedCode = normalizeInternalCableCode(codiceInterno);
  if (!itemId && !normalizedCode) return undefined;
  return { itemId: itemId || null, codiceInterno: normalizedCode };
};
